import React, { useEffect, useMemo, useState } from "react";
import {
  FaBusAlt,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaExternalLinkAlt,
  FaFilter,
  FaSearch,
  FaSyncAlt,
  FaUsers,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import http from "../../api/apiClient";
import Spinner from "../../components/Spinner";
import ErrorPage from "../../components/ErrorPage";
import { useCan } from "../../authz/useCan";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";

const ExternalLinkIcon = FaExternalLinkAlt as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const SearchIcon = FaSearch as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const SyncIcon = FaSyncAlt as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const BusIcon = FaBusAlt as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const FilterIcon = FaFilter as React.ComponentType<React.SVGProps<SVGSVGElement>>;

type OutingStatus = "OPEN" | "COMPLETE" | "EXPIRED" | "UNKNOWN" | string;

type OutingReportRow = {
  outingId: string | null;
  classroomId: string | null;
  classroomName: string | null;
  schoolYearId: string | null;
  schoolYearLabel: string | null;
  outingTitle: string | null;
  outingDate: unknown;
  startDate?: unknown;
  endDate?: unknown;
  startTime: unknown;
  endTime: unknown;
  durationMinutes: number | null;
  totalChildren: number | null;
  submittedCount: number | null;
  missingCount: number | null;
  status: OutingStatus | null;
};

type PageError = {
  message?: string;
  status?: number;
};

const STATUS_LABELS: Record<string, string> = {
  OPEN: "Open",
  COMPLETE: "Compleet",
  COMPLETED: "Compleet",
  EXPIRED: "Verlopen",
  UNKNOWN: "Onbekend",
};

const STATUS_CLASSES: Record<string, string> = {
  OPEN: "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-100",
  COMPLETE:
    "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100",
  COMPLETED:
    "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100",
  EXPIRED:
    "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100",
  UNKNOWN:
    "border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100",
};

const normalizeStatus = (value: OutingStatus | null | undefined) =>
  String(value || "UNKNOWN").trim().toUpperCase();

const statusLabel = (value: OutingStatus | null | undefined) => {
  const normalized = normalizeStatus(value);
  return STATUS_LABELS[normalized] || STATUS_LABELS.UNKNOWN;
};

const pad2 = (value: unknown) => String(value ?? "").padStart(2, "0");

const normalizeDateValue = (value: unknown) => {
  if (!value) return "-";
  if (Array.isArray(value) && value.length >= 3) {
    return `${value[0]}-${pad2(value[1])}-${pad2(value[2])}`;
  }
  if (typeof value === "object") {
    const candidate = value as { year?: unknown; month?: unknown; day?: unknown; dayOfMonth?: unknown };
    if (candidate.year != null && candidate.month != null && (candidate.day != null || candidate.dayOfMonth != null)) {
      return `${candidate.year}-${pad2(candidate.month)}-${pad2(candidate.day ?? candidate.dayOfMonth)}`;
    }
  }
  return String(value);
};

const formatDate = (value: unknown) => {
  const normalized = normalizeDateValue(value);
  if (normalized === "-") return normalized;
  const date = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(date.getTime())) return normalized;
  return new Intl.DateTimeFormat("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const formatDuration = (minutes: number | null) => {
  if (minutes == null || Number.isNaN(minutes)) return "-";
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours <= 0) return `${remainder} min`;
  if (remainder === 0) return `${hours} u`;
  return `${hours} u ${remainder} min`;
};

const getStartDate = (row: OutingReportRow) => row.startDate ?? row.outingDate;
const getEndDate = (row: OutingReportRow) => row.endDate ?? null;

const getDateParts = (value: unknown) => {
  const normalized = normalizeDateValue(value);
  if (normalized === "-") {
    return { day: "—", month: "", year: "" };
  }

  const date = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return { day: "—", month: "", year: "" };
  }

  return {
    day: new Intl.DateTimeFormat("nl-NL", { day: "2-digit" }).format(date),
    month: new Intl.DateTimeFormat("nl-NL", { month: "short" })
      .format(date)
      .replace(".", "")
      .toUpperCase(),
    year: new Intl.DateTimeFormat("nl-NL", { year: "numeric" }).format(date),
  };
};

const getDateRangeLabel = (row: OutingReportRow) => {
  const start = formatDate(getStartDate(row));
  const endValue = getEndDate(row);
  const end = endValue ? formatDate(endValue) : null;

  if (!end || end === "-" || end === start) {
    return start;
  }

  return `${start} – ${end}`;
};

export default function OutingsReport() {
  const navigate = useNavigate();
  const canCrudClassrooms = useCan("school.classrooms", "crud");
  const canManageClassrooms = useCan("school.classrooms", "manage");
  const canManageClassroomsOverview = useCan("school.classrooms.overview", "manage");
  const canOpenClassroom = canCrudClassrooms || canManageClassrooms || canManageClassroomsOverview;
  const [rows, setRows] = useState<OutingReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<PageError | null>(null);
  const [search, setSearch] = useState("");
  const [schoolYear, setSchoolYear] = useState("");
  const [status, setStatus] = useState("");

  const loadRows = async () => {
    setError(null);
    setRefreshing(true);
    try {
      const response = await http.get<OutingReportRow[]>("/reports/outings");
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(mapHttpErrorToMessage(err, (key: string, options?: { defaultValue?: string }) => options?.defaultValue || key));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const schoolYearOptions = useMemo(() => {
    const seen = new Set<string>();
    return rows
      .map((row) => row.schoolYearLabel)
      .filter((label): label is string => Boolean(label && label.trim()))
      .filter((label) => {
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
      })
      .sort();
  }, [rows]);

  const statusOptions = useMemo(() => {
    const seen = new Set<string>();
    return rows
      .map((row) => normalizeStatus(row.status))
      .filter((value) => {
        if (seen.has(value)) return false;
        seen.add(value);
        return true;
      })
      .sort((a, b) => statusLabel(a).localeCompare(statusLabel(b), "nl-NL"));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch =
        !term ||
        [row.classroomName, row.outingTitle]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      const matchesSchoolYear = !schoolYear || row.schoolYearLabel === schoolYear;
      const matchesStatus = !status || normalizeStatus(row.status) === status;
      return matchesSearch && matchesSchoolYear && matchesStatus;
    });
  }, [rows, schoolYear, search, status]);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorPage message={error.message || "Er is iets misgegaan."} status={error.status} onRetry={() => { void loadRows(); }} />;
  }

  return (
    <main className="lds-report-enter px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1400px] rounded-[32px] border border-slate-200/80 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950 sm:p-5">
        <div className="space-y-6">
          <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-6 py-6 shadow-sm dark:border-indigo-900 dark:from-indigo-950/50 dark:via-slate-950 dark:to-sky-950/40 sm:px-8">
            <BusIcon
              aria-hidden="true"
              className="pointer-events-none absolute right-10 top-1/2 hidden h-20 w-20 -translate-y-1/2 text-indigo-200 dark:text-indigo-900/40 sm:block"
            />

            <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-center gap-5">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
                  <BusIcon aria-hidden="true" />
                </div>

                <div className="min-w-0 max-w-3xl">
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-indigo-700 dark:text-indigo-300">
                    RAPPORTEN
                  </p>
                  <h1 className="mt-1 text-3xl font-bold text-slate-950 dark:text-white">
                    Uitjes
                  </h1>
                  <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                    Bekijk uitjes, reacties en ontbrekende toestemmingen per klas en schooljaar.
                  </p>
                </div>
              </div>

            </div>
          </section>

          <section className="overflow-visible rounded-[24px] border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950">
            <div className="flex flex-col gap-4 border-b border-slate-200/80 px-5 py-5 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between lg:px-6">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
                  <FilterIcon aria-hidden="true" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-950 dark:text-white">
                    Zoeken en filters
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Filter op klas, uitje, schooljaar en status.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => void loadRows()}
                disabled={refreshing}
                className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:border-indigo-300 hover:text-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-indigo-600 dark:hover:text-indigo-300 dark:focus:ring-indigo-950"
              >
                <SyncIcon className={refreshing ? "animate-spin" : ""} />
                Verversen
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 px-5 py-5 md:grid-cols-2 xl:grid-cols-4 lg:px-6">
              <label className="flex min-w-0 flex-col gap-1.5 xl:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Zoeken
                </span>
                <div className="relative">
                  <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Klas of uitje"
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm font-medium text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                  />
                </div>
              </label>

              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Schooljaar
                </span>
                <select
                  value={schoolYear}
                  onChange={(event) => setSchoolYear(event.target.value)}
                  className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                >
                  <option value="">Alle schooljaren</option>
                  {schoolYearOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Status
                </span>
                <select
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                >
                  <option value="">Alle statussen</option>
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {statusLabel(option)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <section className="overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 px-5 py-3.5 dark:border-slate-800">
              <div>
                <h2 className="text-base font-bold text-slate-950 dark:text-white">
                  Uitjesoverzicht
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {filteredRows.length} van {rows.length} uitjes
                </p>
              </div>
            </div>

            {filteredRows.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                Geen uitjes gevonden voor de gekozen filters.
              </div>
            ) : (
              <div className="divide-y divide-slate-200/80 dark:divide-slate-800">
                {filteredRows.map((row, index) => {
                  const normalizedStatus = normalizeStatus(row.status);
                  const dateParts = getDateParts(row.outingDate);
                  const submitted = row.submittedCount ?? 0;
                  const missing = row.missingCount ?? 0;
                  const total = row.totalChildren ?? 0;

                  return (
                    <article
                      key={`${row.outingId || "outing"}-${row.classroomId || "classroom"}-${index}`}
                      className="grid gap-3 px-4 py-3.5 transition hover:bg-slate-50/80 dark:hover:bg-slate-900/60 md:grid-cols-[64px_minmax(0,1fr)_auto] md:items-center md:px-5"
                    >
                      <div className="flex lg:justify-center">
                        <div className="flex h-[58px] w-[54px] shrink-0 flex-col items-center justify-center rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-sky-50 text-center shadow-sm dark:border-indigo-900 dark:from-indigo-950/70 dark:to-sky-950/50">
                          <span className="text-[9px] font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                            {dateParts.month}
                          </span>
                          <span className="text-xl font-extrabold leading-none text-slate-950 dark:text-white">
                            {dateParts.day}
                          </span>
                          <span className="mt-0.5 text-[9px] font-semibold text-slate-500 dark:text-slate-400">
                            {dateParts.year}
                          </span>
                        </div>
                      </div>

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="truncate text-sm font-bold text-slate-950 dark:text-white">
                            {row.outingTitle || "-"}
                          </h3>
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                              STATUS_CLASSES[normalizedStatus] ||
                              STATUS_CLASSES.UNKNOWN
                            }`}
                          >
                            {statusLabel(row.status)}
                          </span>
                        </div>

                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
                          <span className="font-semibold text-slate-800 dark:text-slate-200">
                            {row.classroomName || "-"}
                          </span>
                          <span>{row.schoolYearLabel || "-"}</span>
                          <span className="inline-flex items-center gap-1.5">
                            <FaCalendarAlt aria-hidden="true" className="text-slate-400" />
                            {getDateRangeLabel(row)}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <FaClock aria-hidden="true" className="text-slate-400" />
                            {formatDuration(row.durationMinutes)}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 md:flex-nowrap md:justify-end">
                        <div className="grid min-w-[210px] grid-cols-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                          <div className="px-2.5 py-1.5 text-center">
                            <div className="flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                              <FaUsers aria-hidden="true" />
                              Verwacht
                            </div>
                            <div className="mt-0.5 text-sm font-bold text-slate-950 dark:text-white">
                              {total || "-"}
                            </div>
                          </div>

                          <div className="border-x border-slate-200 px-2.5 py-1.5 text-center dark:border-slate-800">
                            <div className="flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                              <FaCheckCircle aria-hidden="true" />
                              Reacties
                            </div>
                            <div className="mt-0.5 text-sm font-bold text-emerald-700 dark:text-emerald-300">
                              {submitted}
                            </div>
                          </div>

                          <div className="px-2.5 py-1.5 text-center">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                              Ontbrekend
                            </div>
                            <div
                              className={`mt-0.5 text-sm font-bold ${
                                missing > 0
                                  ? "text-rose-700 dark:text-rose-300"
                                  : "text-slate-950 dark:text-white"
                              }`}
                            >
                              {missing}
                            </div>
                          </div>
                        </div>

                        {canOpenClassroom && row.classroomId ? (
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/school/classrooms/${row.classroomId}/details`
                              )
                            }
                            className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-indigo-200 dark:hover:border-indigo-600 dark:hover:bg-indigo-950/40"
                          >
                            <ExternalLinkIcon />
                            Klas
                          </button>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
